import { isAxiosError } from 'axios'
import { AlertCircle, Check, Pencil, Plus, RefreshCw, RotateCcw, ShoppingBag, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { OnlineCatalogOptionSelect } from '@/app/components/catalog/OnlineCatalogOptionSelect'
import { ConfirmationModal } from '@/app/components/ui/ConfirmationModal'
import { GlobalFilterBar } from '@/app/components/ui/GlobalFilterBar'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { GlobalPaginator } from '@/app/components/ui/global-paginator'
import { Input } from '@/app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select'
import { TableSkeleton } from '@/app/components/ui/table-skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs'
import {
  type CatalogAction,
  type CatalogChannel,
  type CatalogOption,
  type CatalogPage,
  type CatalogProduct,
  type CatalogRule,
  type CatalogRuleType,
  onlineCatalogService,
} from '@/app/services/online-catalog.service'
import { useSessionStore } from '@/app/store/auth/auth.store'

const pageSize = 20
const ruleTypes: CatalogRuleType[] = ['PRODUCT', 'CATEGORY', 'DEPARTMENT', 'PRODUCT_TYPE']
const typeLabels: Record<CatalogRuleType | 'DEFAULT', string> = {
  PRODUCT: 'Producto', CATEGORY: 'Categoría', DEPARTMENT: 'Departamento',
  PRODUCT_TYPE: 'Tipo de producto', DEFAULT: 'Acción predeterminada',
}
const optionTypes: Record<Exclude<CatalogRuleType, 'PRODUCT'>, 'categories' | 'departments' | 'product-types'> = {
  CATEGORY: 'categories', DEPARTMENT: 'departments', PRODUCT_TYPE: 'product-types',
}

function message(error: unknown, fallback: string): string {
  return isAxiosError(error) ? (error.response?.data?.message || fallback) : fallback
}

function PageNavigation({ pagination, onPageChange }: {
  pagination: CatalogPage<unknown>['pagination']
  onPageChange: (page: number) => void
}) {
  const from = pagination.total ? (pagination.current_page - 1) * pagination.per_page + 1 : 0
  const to = Math.min(pagination.total, pagination.current_page * pagination.per_page)
  return <GlobalPaginator
    totalItems={pagination.total}
    itemsPerPage={pagination.per_page}
    currentPage={pagination.current_page}
    from={from}
    to={to}
    onPageChange={onPageChange}
  />
}

function ChannelPanel({ channel, canEdit, canStatus, onChanged }: {
  channel: CatalogChannel
  canEdit: boolean
  canStatus: boolean
  onChanged: () => void
}) {
  const [name, setName] = useState(channel.name)
  const [defaultAction, setDefaultAction] = useState<CatalogAction>(channel.default_action)
  const [saving, setSaving] = useState(false)
  const [confirmStatus, setConfirmStatus] = useState(false)
  const [confirmDefault, setConfirmDefault] = useState(false)

  useEffect(() => {
    setName(channel.name)
    setDefaultAction(channel.default_action)
  }, [channel])

  const save = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      await onlineCatalogService.updateChannel({ name: name.trim(), default_action: defaultAction })
      toast.success('Canal actualizado')
      setConfirmDefault(false)
      onChanged()
    } catch (error) {
      toast.error(message(error, 'No se pudo actualizar el canal'))
    } finally { setSaving(false) }
  }

  const toggleStatus = async () => {
    setSaving(true)
    try {
      await onlineCatalogService.updateStatus(!channel.is_active)
      toast.success('Estado del canal actualizado')
      setConfirmStatus(false)
      onChanged()
    } catch (error) {
      toast.error(message(error, 'No se pudo cambiar el estado del canal'))
    } finally { setSaving(false) }
  }

  return <section className="space-y-5 rounded-lg border border-border bg-card p-5 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-header-foreground">Canal de distribución</h2>
        <p className="text-sm text-muted-foreground">Código fijo: {channel.code}</p>
      </div>
      <Badge className={channel.is_active ? 'bg-[#DCFCE6] text-[#008438]' : 'bg-[#FFE2E2] text-[#C10008]'}>
        {channel.is_active ? 'Activo' : 'Inactivo'}
      </Badge>
    </div>
    {!channel.is_active && <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      El canal está inactivo: ningún producto se muestra públicamente, aunque sus reglas indiquen “Incluir”.
    </p>}
    <div className="grid gap-4 md:grid-cols-2">
      <label className="text-sm font-medium text-foreground">Nombre
        <Input className="mt-1" value={name} onChange={(event) => setName(event.target.value)} disabled={!canEdit} maxLength={120} />
      </label>
      <div className="space-y-1"><span className="block text-sm font-medium text-foreground">Acción predeterminada</span>
        <Select value={defaultAction} onValueChange={(value) => setDefaultAction(value as CatalogAction)} disabled={!canEdit}>
          <SelectTrigger aria-label="Acción predeterminada"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="INCLUDE">Incluir</SelectItem><SelectItem value="EXCLUDE">Excluir</SelectItem></SelectContent>
        </Select>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
      {ruleTypes.map((type) => <Badge key={type} variant="secondary">{typeLabels[type]}: {channel.rule_counts[type] || 0}</Badge>)}
    </div>
    <div className="flex flex-wrap gap-2">
      {canEdit && <Button disabled={saving || !name.trim()} onClick={() => defaultAction !== channel.default_action ? setConfirmDefault(true) : void save()}>
        Guardar configuración
      </Button>}
      {canStatus && <Button variant="outline" disabled={saving} onClick={() => setConfirmStatus(true)}>
        {channel.is_active ? 'Desactivar canal' : 'Activar canal'}
      </Button>}
    </div>
    <ConfirmationModal isOpen={confirmDefault} onClose={() => setConfirmDefault(false)} onConfirm={save}
      preset="toggleStatus" itemName="acción predeterminada" isLoading={saving} confirmText="Sí, cambiar acción"
      description="Cambiar la acción predeterminada puede publicar u ocultar todos los productos sin una regla específica." />
    <ConfirmationModal isOpen={confirmStatus} onClose={() => setConfirmStatus(false)} onConfirm={toggleStatus}
      preset="toggleStatus" itemName="canal de tienda" isLoading={saving}
      description={channel.is_active ? 'Al desactivar el canal, todo el catálogo dejará de mostrarse públicamente.' : 'Al activar el canal, volverán a aplicarse las reglas de publicación.'} />
  </section>
}

function ProductsPanel({ canEdit, version, onChanged }: { canEdit: boolean; version: number; onChanged: () => void }) {
  const [result, setResult] = useState<CatalogPage<CatalogProduct> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [published, setPublished] = useState<'all' | 'true' | 'false'>('all')
  const [category, setCategory] = useState<CatalogOption | null>(null)
  const [department, setDepartment] = useState<CatalogOption | null>(null)
  const [productType, setProductType] = useState<CatalogOption | null>(null)
  const [pending, setPending] = useState<number | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(false)
    void onlineCatalogService.products({ search: search || undefined, page, per_page: pageSize,
      category_id: category?.id, department_id: department?.id, product_type_id: productType?.id,
      published: published === 'all' ? undefined : published === 'true',
    }).then((value) => { if (active) setResult(value) })
      .catch(() => { if (active) setError(true) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [category, department, page, productType, published, retry, search, version])

  const mutate = async (product: CatalogProduct, action: CatalogAction | 'INHERIT') => {
    setPending(product.id)
    try {
      await onlineCatalogService.syncRules(action === 'INHERIT'
        ? { remove: [{ type: 'PRODUCT', reference_id: product.id }] }
        : { upsert: [{ type: 'PRODUCT', reference_id: product.id, action }] })
      toast.success('Publicación actualizada')
      onChanged()
    } catch (failure) {
      toast.error(message(failure, 'No se pudo actualizar la publicación'))
    } finally { setPending(null) }
  }

  const hasFilters = Boolean(search || category || department || productType || published !== 'all')
  const clearFilters = () => { setSearch(''); setCategory(null); setDepartment(null); setProductType(null); setPublished('all'); setPage(1) }

  return <section className="space-y-4">
    <p className="text-sm text-muted-foreground">Se muestran productos elegibles para tu compañía. Una regla de producto tiene prioridad sobre categoría, departamento y tipo.</p>
    <GlobalFilterBar show={['search']} values={{ search }} onChange={(changes) => {
      if (changes.search !== undefined) { setSearch(changes.search); setPage(1) }
    }} customOptions={{ searchPlaceholder: 'Buscar por nombre, SKU o código de barras' }}>
      <OnlineCatalogOptionSelect label="Categoría" kind="categories" selected={category} onSelect={(value) => { setCategory(value); setPage(1) }} onClear={() => { setCategory(null); setPage(1) }} />
      <OnlineCatalogOptionSelect label="Departamento" kind="departments" selected={department} onSelect={(value) => { setDepartment(value); setPage(1) }} onClear={() => { setDepartment(null); setPage(1) }} />
      <OnlineCatalogOptionSelect label="Tipo de producto" kind="product-types" selected={productType} onSelect={(value) => { setProductType(value); setPage(1) }} onClear={() => { setProductType(null); setPage(1) }} />
      <div className="space-y-1"><span className="block text-sm font-medium text-card-foreground">Publicación efectiva</span>
        <Select value={published} onValueChange={(value) => { setPublished(value as typeof published); setPage(1) }}>
          <SelectTrigger aria-label="Publicación efectiva"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="true">Incluidos</SelectItem><SelectItem value="false">Excluidos</SelectItem></SelectContent>
        </Select>
      </div>
      {hasFilters && <Button type="button" variant="outline" onClick={clearFilters}>Limpiar filtros</Button>}
    </GlobalFilterBar>
    {error && <div role="alert" className="rounded-lg border border-border bg-card p-10 text-center">
      <AlertCircle className="mx-auto mb-3 h-8 w-8 text-destructive" />
      <p className="font-medium">No se pudieron cargar los productos</p>
      <Button variant="outline" className="mt-4" onClick={() => setRetry((value) => value + 1)}><RefreshCw className="mr-2 h-4 w-4" />Reintentar</Button>
    </div>}
    {loading && <TableSkeleton headers={['Producto', 'Clasificación', 'Decisión', 'Origen', 'Acciones']} columns={['text', 'text', 'badge', 'text', 'actions']} rows={6} />}
    {!loading && !error && result && <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
      <table className="w-full text-left text-sm text-card-foreground"><thead className="bg-table-header-bg text-table-header-foreground"><tr>
        <th className="p-3">Producto</th><th className="p-3">Clasificación</th><th className="p-3">Decisión</th><th className="p-3">Origen</th><th className="p-3">Acciones</th>
      </tr></thead><tbody>
        {result.items.map((product) => <tr key={product.id} className="border-t border-border align-top">
          <td className="p-3"><span className="font-medium text-card-foreground">{product.name}</span><span className="block text-card-foreground">{product.sku || `#${product.id}`}</span></td>
          <td className="p-3">{[product.category?.name, product.department?.name, product.product_type?.name].filter(Boolean).join(' · ')}</td>
          <td className="p-3"><Badge className={product.publication.is_published ? 'bg-[#DCFCE6] text-[#008438]' : 'bg-[#FFE2E2] text-[#C10008]'}>
            {product.publication.is_published ? 'Incluido' : 'Excluido'}
          </Badge></td>
          <td className="p-3 text-card-foreground">{typeLabels[product.publication.source_type]}</td>
          <td className="whitespace-nowrap p-3">{canEdit && <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" title="Incluir" aria-label={`Incluir ${product.name}`}
              disabled={pending === product.id} onClick={() => void mutate(product, 'INCLUDE')}><Check className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" title="Excluir" aria-label={`Excluir ${product.name}`}
              disabled={pending === product.id} onClick={() => void mutate(product, 'EXCLUDE')}><X className="h-4 w-4" /></Button>
            {product.publication.source_type === 'PRODUCT' && <Button size="icon" variant="ghost" title="Heredar" aria-label={`Heredar ${product.name}`}
              disabled={pending === product.id} onClick={() => void mutate(product, 'INHERIT')}><RotateCcw className="h-4 w-4" /></Button>}
          </div>}</td>
        </tr>)}
        {result.items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">No hay productos para estos filtros.</td></tr>}
      </tbody></table>
      <PageNavigation pagination={result.pagination} onPageChange={setPage} />
    </div>}
  </section>
}

function RulesPanel({ canEdit, version, onChanged }: { canEdit: boolean; version: number; onChanged: () => void }) {
  const [result, setResult] = useState<CatalogPage<CatalogRule> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<CatalogRuleType | ''>('')
  const [actionFilter, setActionFilter] = useState<CatalogAction | ''>('')
  const [newType, setNewType] = useState<CatalogRuleType>('CATEGORY')
  const [newAction, setNewAction] = useState<CatalogAction>('EXCLUDE')
  const [reference, setReference] = useState<CatalogOption | null>(null)
  const [confirm, setConfirm] = useState<{ rule: CatalogRule; operation: 'change' | 'remove' } | null>(null)
  const [confirmAdd, setConfirmAdd] = useState(false)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(false)
    void onlineCatalogService.rules({ search: search || undefined, type: typeFilter || undefined,
      action: actionFilter || undefined, page, per_page: pageSize })
      .then((value) => { if (active) setResult(value) })
      .catch(() => { if (active) setError(true) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [actionFilter, page, retry, search, typeFilter, version])

  const addRule = async () => {
    if (!reference) return
    setPending(true)
    try {
      await onlineCatalogService.syncRules({ upsert: [{ type: newType, reference_id: reference.id, action: newAction }] })
      toast.success('Regla guardada')
      setReference(null)
      setConfirmAdd(false)
      onChanged()
    } catch (failure) { toast.error(message(failure, 'No se pudo guardar la regla')) }
    finally { setPending(false) }
  }

  const applyConfirmed = async () => {
    if (!confirm) return
    setPending(true)
    const { rule, operation } = confirm
    try {
      await onlineCatalogService.syncRules(operation === 'remove'
        ? { remove: [{ type: rule.type, reference_id: rule.reference_id }] }
        : { upsert: [{ type: rule.type, reference_id: rule.reference_id, action: rule.action === 'INCLUDE' ? 'EXCLUDE' : 'INCLUDE' }] })
      toast.success('Reglas actualizadas')
      setConfirm(null)
      if (operation === 'remove' && result?.items.length === 1 && page > 1) setPage(page - 1)
      onChanged()
    } catch (failure) { toast.error(message(failure, 'No se pudo actualizar la regla')) }
    finally { setPending(false) }
  }

  const hasFilters = Boolean(search || typeFilter || actionFilter)
  const clearFilters = () => { setSearch(''); setTypeFilter(''); setActionFilter(''); setPage(1) }

  return <section className="space-y-5">
    <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
      Las reglas de categoría, departamento y tipo pueden afectar muchos productos. Precedencia: producto → categoría → departamento → tipo → acción predeterminada.
    </p>
    {canEdit && <div className="space-y-4 rounded-lg border border-border bg-card p-4 shadow-sm">
      <h2 className="font-semibold text-header-foreground">Agregar o reemplazar regla</h2>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <div className="space-y-1"><span className="block text-sm font-medium">Tipo</span>
          <Select value={newType} onValueChange={(value) => { setNewType(value as CatalogRuleType); setReference(null) }}>
            <SelectTrigger aria-label="Tipo de nueva regla"><SelectValue /></SelectTrigger>
            <SelectContent>{ruleTypes.map((type) => <SelectItem key={type} value={type}>{typeLabels[type]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <OnlineCatalogOptionSelect label={newType === 'PRODUCT' ? 'Producto' : 'Referencia'}
          kind={newType === 'PRODUCT' ? 'products' : optionTypes[newType]}
          selected={reference} onSelect={setReference} onClear={() => setReference(null)} />
        <div className="space-y-1"><span className="block text-sm font-medium">Acción</span>
          <Select value={newAction} onValueChange={(value) => setNewAction(value as CatalogAction)}>
            <SelectTrigger aria-label="Acción de nueva regla"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="INCLUDE">Incluir</SelectItem><SelectItem value="EXCLUDE">Excluir</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <Button disabled={!reference || pending} onClick={() => newType === 'PRODUCT' ? void addRule() : setConfirmAdd(true)}>
        <Plus className="mr-2 h-4 w-4" />Guardar regla
      </Button>
    </div>}
    <GlobalFilterBar show={['search']} values={{ search }} onChange={(changes) => {
      if (changes.search !== undefined) { setSearch(changes.search); setPage(1) }
    }} customOptions={{ searchPlaceholder: 'Buscar referencia' }}>
      <Select value={typeFilter || 'all'} onValueChange={(value) => { setTypeFilter(value === 'all' ? '' : value as CatalogRuleType); setPage(1) }}>
        <SelectTrigger aria-label="Filtrar tipo de regla"><SelectValue placeholder="Todos los tipos" /></SelectTrigger>
        <SelectContent><SelectItem value="all">Todos los tipos</SelectItem>{ruleTypes.map((type) => <SelectItem key={type} value={type}>{typeLabels[type]}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={actionFilter || 'all'} onValueChange={(value) => { setActionFilter(value === 'all' ? '' : value as CatalogAction); setPage(1) }}>
        <SelectTrigger aria-label="Filtrar acción de regla"><SelectValue placeholder="Todas las acciones" /></SelectTrigger>
        <SelectContent><SelectItem value="all">Todas las acciones</SelectItem><SelectItem value="INCLUDE">Incluir</SelectItem><SelectItem value="EXCLUDE">Excluir</SelectItem></SelectContent>
      </Select>
      {hasFilters && <Button type="button" variant="outline" onClick={clearFilters}>Limpiar filtros</Button>}
    </GlobalFilterBar>
    {error && <div role="alert" className="rounded-lg border border-border bg-card p-10 text-center">
      <AlertCircle className="mx-auto mb-3 h-8 w-8 text-destructive" />
      <p className="font-medium">No se pudieron cargar las reglas</p>
      <Button variant="outline" className="mt-4" onClick={() => setRetry((value) => value + 1)}><RefreshCw className="mr-2 h-4 w-4" />Reintentar</Button>
    </div>}
    {loading && <TableSkeleton headers={['Referencia', 'Tipo', 'Acción', 'Acciones']} columns={['text', 'text', 'badge', 'actions']} rows={6} />}
    {!loading && !error && result && <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
      <table className="w-full text-left text-sm"><thead className="bg-table-header-bg text-table-header-foreground"><tr>
        <th className="p-3">Referencia</th><th className="p-3">Tipo</th><th className="p-3">Acción</th><th className="p-3">Acciones</th>
      </tr></thead><tbody>
        {result.items.map((rule) => <tr key={rule.id} className="border-t border-border"><td className="p-3 font-medium">{rule.reference_name || `#${rule.reference_id}`}</td>
          <td className="p-3 text-muted-foreground">{typeLabels[rule.type]}</td>
          <td className="p-3"><Badge className={rule.action === 'INCLUDE' ? 'bg-[#DCFCE6] text-[#008438]' : 'bg-[#FFE2E2] text-[#C10008]'}>
            {rule.action === 'INCLUDE' ? 'Incluir' : 'Excluir'}
          </Badge></td>
          <td className="p-3">{canEdit && <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" title="Cambiar acción" aria-label={`Cambiar acción de ${rule.reference_name || rule.reference_id}`}
              onClick={() => setConfirm({ rule, operation: 'change' })}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" title="Quitar regla" aria-label={`Quitar regla de ${rule.reference_name || rule.reference_id}`}
              onClick={() => setConfirm({ rule, operation: 'remove' })}><Trash2 className="h-4 w-4" /></Button>
          </div>}</td></tr>)}
        {result.items.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-muted-foreground">No hay reglas para estos filtros.</td></tr>}
      </tbody></table><PageNavigation pagination={result.pagination} onPageChange={setPage} />
    </div>}
    <ConfirmationModal isOpen={confirm !== null} onClose={() => setConfirm(null)} onConfirm={applyConfirmed}
      preset={confirm?.operation === 'remove' ? 'delete' : 'toggleStatus'} itemName="regla de publicación" isLoading={pending}
      description={`¿Confirmas ${confirm?.operation === 'remove' ? 'quitar' : 'cambiar'} la regla de ${confirm ? typeLabels[confirm.rule.type].toLowerCase() : ''}? Puede afectar a varios productos.`} />
    <ConfirmationModal isOpen={confirmAdd} onClose={() => setConfirmAdd(false)} onConfirm={addRule}
      preset="toggleStatus" itemName="regla de publicación" isLoading={pending} confirmText="Sí, guardar regla"
      description={`La regla de ${typeLabels[newType].toLowerCase()} puede afectar a varios productos. ¿Confirmas ${newAction === 'INCLUDE' ? 'incluir' : 'excluir'} esta referencia?`} />
  </section>
}

export function PublishedProducts() {
  const canEdit = useSessionStore((state) => state.canAction('ecommerce.catalog.edit'))
  const canStatus = useSessionStore((state) => state.canAction('ecommerce.catalog.status'))
  const [tab, setTab] = useState<'channel' | 'products' | 'rules'>('products')
  const [channel, setChannel] = useState<CatalogChannel | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(false)
    void onlineCatalogService.channel()
      .then((value) => { if (active) setChannel(value) })
      .catch(() => { if (active) setError(true) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [version])

  const refresh = () => setVersion((value) => value + 1)

  return <div className="space-y-6 p-6">
    <div className="flex items-center gap-3"><ShoppingBag className="h-6 w-6 text-primary" />
      <div><h1 className="text-2xl font-semibold text-header-foreground">Productos Publicados</h1>
        <p className="mt-1 text-sm text-muted-foreground">Administra lo que se muestra en la tienda en línea.</p></div>
    </div>
    <Tabs value={tab} onValueChange={(value) => setTab(value as typeof tab)}>
      <TabsList aria-label="Secciones de publicación">
        <TabsTrigger value="products">Productos</TabsTrigger>
        <TabsTrigger value="rules">Reglas</TabsTrigger>
        <TabsTrigger value="channel">Canal</TabsTrigger>
      </TabsList>
    {loading && !channel && <p className="text-slate-600">Cargando canal...</p>}
    {error && <div role="alert" className="rounded-lg border border-border bg-card p-10 text-center">
      <AlertCircle className="mx-auto mb-3 h-8 w-8 text-destructive" />
      <p className="font-medium">No se pudo cargar el canal</p>
      <Button variant="outline" className="mt-4" onClick={refresh}><RefreshCw className="mr-2 h-4 w-4" />Reintentar</Button>
    </div>}
    {channel && !error && <>
      {!channel.is_active && tab !== 'channel' && <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        El canal está inactivo: las decisiones de esta pantalla no son visibles públicamente hasta activarlo.
      </p>}
      <TabsContent value="channel">{tab === 'channel' && <ChannelPanel channel={channel} canEdit={canEdit} canStatus={canStatus} onChanged={refresh} />}</TabsContent>
      <TabsContent value="products">{tab === 'products' && <ProductsPanel canEdit={canEdit} version={version} onChanged={refresh} />}</TabsContent>
      <TabsContent value="rules">{tab === 'rules' && <RulesPanel canEdit={canEdit} version={version} onChanged={refresh} />}</TabsContent>
    </>}
    </Tabs>
  </div>
}
